import { useEffect, useState } from 'react';
import { FileResponse, FileSource } from '../interfaces/FileResponse';
import { isPdfItem } from '../search/use-sheet-source';
import { useCollectionService } from '../../services/collection.service';

/** One screen in stage mode: an image, a whole file, or a single page of a collection PDF. */
export interface StageEntry {
    item: FileResponse,
    itemIndex: number,
    page?: number,
    pageCount?: number,
}

// The API renders collection PDFs page by page; other sources keep the whole-file behaviour
const hasServerPages = (item: FileResponse) => item.source === FileSource.Dropbox && isPdfItem(item);

const pageCountCache = new Map<string, Promise<number>>();
const pageImageCache = new Map<string, Promise<string>>();

/**
 * Expands every collection PDF into one entry per page. Returns undefined until the page counts are known,
 * so the indexes don't shift while the musician is already paging. A PDF whose count fails stays a single entry.
 */
export function useStageEntries(items: FileResponse[]): StageEntry[] | undefined {
    const [entries, setEntries] = useState<StageEntry[]>();

    const collectionService = useCollectionService();

    useEffect(() => {
        let active = true;

        const countPages = (item: FileResponse) => {
            if (!hasServerPages(item))
                return Promise.resolve(undefined);

            let pending = pageCountCache.get(item.serverPath);
            if (!pending) {
                pending = collectionService.getPageCount(item.serverPath);
                pageCountCache.set(item.serverPath, pending);
                pending.catch(() => pageCountCache.delete(item.serverPath));
            }

            return pending.catch(() => undefined);
        };

        Promise.all(items.map(countPages)).then(counts => {
            if (!active) return;

            setEntries(items.flatMap((item, itemIndex) => {
                const pageCount = counts[itemIndex];

                if (!pageCount || pageCount < 1)
                    return [{ item, itemIndex }];

                return Array.from({ length: pageCount }, (_, i) => ({ item, itemIndex, page: i + 1, pageCount }));
            }));
        });

        return () => { active = false; };
        // The service object is recreated on every render; the items are what matter here
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);

    return entries;
}

// Request a width that covers the screen (in device pixels), rounded so pages are reused across small resizes
const pageWidth = () => {
    const ratio = window.devicePixelRatio || 1;
    const portraitFit = Math.min(window.innerWidth, window.innerHeight / 1.414);
    const needed = Math.ceil((portraitFit * ratio) / 400) * 400;

    return Math.min(Math.max(needed, 800), 2000);
};

/** Object URL of one rendered PDF page, cached for the session. */
export function usePdfPageImage(item: FileResponse, page: number | undefined): string | undefined {
    const [src, setSrc] = useState<string>();

    const collectionService = useCollectionService();

    useEffect(() => {
        if (!page) return;

        let active = true;
        const width = pageWidth();
        const key = `${item.serverPath}|${page}|${width}`;

        let pending = pageImageCache.get(key);
        if (!pending) {
            pending = collectionService.getPage(item.serverPath, page, width).then(blob => URL.createObjectURL(blob));
            pageImageCache.set(key, pending);
            pending.catch(() => pageImageCache.delete(key));
        }

        pending
            .then(url => { if (active) setSrc(url); })
            .catch(() => { });

        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.serverPath, page]);

    return src;
}
